import { z } from 'zod';
/**
 * TDS and TCS - tax withheld at source, on both sides of a sale.
 *
 * ── WHY BOTH, AND WHY THEY LIVE IN DIFFERENT PLACES ──
 * They are mirror images and it matters structurally:
 *
 *   TDS  the BUYER deducts it when paying, and the seller receives less than
 *        the invoice. So it belongs on the PAYMENT, and its whole job is to
 *        let an invoice settle in full on less cash.
 *   TCS  the SELLER collects it on top of the invoice, and the buyer pays more.
 *        So it belongs on the DOCUMENT, and adds to what is owed.
 *
 * Modelling TDS as "a discount" or "a write-off" is the mistake to avoid: the
 * money was not forgiven, it was paid to the government on the seller's behalf
 * and is creditable against their own tax. It has to be recoverable by section
 * and quarter, which is what the registers are for.
 *
 * ── THE TWO BASES ARE DIFFERENT, AND THAT IS NOT A BUG ──
 * TDS is computed on the value EXCLUDING GST wherever GST is shown separately
 * on the invoice - CBDT Circular 23/2017. TCS on sale of goods was computed on
 * the consideration INCLUDING GST, because it attaches to money received rather
 * than to value supplied (Circular 17/2020).
 *
 * Every implementation that gets this wrong gets it wrong by using one base for
 * both. `tdsBaseFor` and `tcsBaseFor` exist so the choice is made once, in one
 * place, with a test on each.
 *
 * ── THESE RATES ARE DEFAULTS, NOT THE SOURCE OF TRUTH ──
 * They change with every Finance Act, and this table is a convenience for the
 * person entering a deduction - never an authority. Two consequences that are
 * deliberate:
 *
 *   1. The rate and the amount are SNAPSHOTTED onto the payment or document
 *      when it is recorded. Nothing is ever recomputed from this table, so
 *      editing a rate here cannot restate a quarter already filed.
 *   2. The AMOUNT DEDUCTED IS ENTERED, NOT COMPUTED, for TDS. The deductor
 *      decides what they deduct, and they are sometimes wrong or use a
 *      different base; recording what actually arrived is the only way the
 *      figures reconcile with Form 26AS. The computed value is offered as a
 *      suggestion beside it.
 *
 * VERIFY AGAINST THE CURRENT FINANCE ACT BEFORE RELYING ON A DEFAULT. The
 * `since` and `until` fields are what a reviewer should check first.
 */
/**
 * The width of the `tds_section` and `tcs_section` columns.
 *
 * Exported so a test can assert every code in the catalogue fits. The first
 * version of this was twelve, which fit every TDS section and none of the TCS
 * sub-clauses - and the failure was silent: the section was rejected before the
 * row was written, so an invoice with TCS on it did not save and the register
 * that should have reported it reported nothing.
 */
export const SECTION_CODE_MAX_LENGTH = 20;
/**
 * TDS sections a small business actually meets on its RECEIVABLES - that is,
 * the ones a customer deducts when paying them.
 *
 * Deliberately not the full Chapter XVII-B. Salary (192), which every business
 * also operates, is payroll and Fint has no payroll; listing it here would
 * suggest a feature that does not exist.
 */
export const TDS_SECTIONS = [
    {
        code: '194J',
        label: 'Professional or technical fees',
        rate: 10,
        threshold: 50000,
        note: 'Technical services, call centres and film royalties are 2%, not 10% - override the rate.',
    },
    {
        code: '194C',
        label: 'Contracts and sub-contracts',
        rate: 2,
        threshold: 100000,
        note: 'An individual or HUF contractor is 1%. The threshold is also ₹30,000 for a single contract.',
    },
    { code: '194H', label: 'Commission or brokerage', rate: 2, threshold: 20000 },
    { code: '194I(a)', label: 'Rent - plant and machinery', rate: 2, threshold: 600000 },
    { code: '194I(b)', label: 'Rent - land, building or furniture', rate: 10, threshold: 600000 },
    {
        code: '194Q',
        label: 'Purchase of goods',
        rate: 0.1,
        threshold: 5000000,
        note: 'Deducted by a buyer whose turnover exceeded ₹10 crore, on value above ₹50 lakh. ' +
            'Mutually exclusive with TCS u/s 206C(1H) - if the buyer deducts here, do not collect TCS.',
    },
    { code: '194A', label: 'Interest other than on securities', rate: 10, threshold: 50000 },
    {
        code: '206AA',
        label: 'No PAN furnished',
        rate: 20,
        note: 'The higher rate that applies when the deductee has not given a PAN.',
    },
];
/**
 * TCS sections a seller collects.
 *
 * ── READ THE NOTE ON 206C(1H) ──
 * It is believed to have been OMITTED with effect from 1 April 2025, to remove
 * the overlap with 194Q - the buyer deducts instead of the seller collecting.
 * It is kept here rather than deleted because a business still recording or
 * correcting an earlier year needs it, and because deleting a section would
 * make historical records unreadable. Verify the position for the year you are
 * filing before using it.
 */
export const TCS_SECTIONS = [
    {
        code: '206C(1H)',
        label: 'Sale of goods',
        rate: 0.1,
        threshold: 5000000,
        until: '2025-04-01',
        note: 'Believed omitted from 1 April 2025 in favour of 194Q. Applied to a seller whose ' +
            'turnover exceeded ₹10 crore, on receipts above ₹50 lakh from one buyer. Unlike TDS, ' +
            'the base INCLUDES GST.',
    },
    { code: '206C(1)-scrap', label: 'Scrap', rate: 1 },
    { code: '206C(1)-timber', label: 'Timber', rate: 2.5 },
    { code: '206C(1)-tendu', label: 'Tendu leaves', rate: 5 },
    { code: '206C(1)-minerals', label: 'Coal, lignite and iron ore', rate: 1 },
    { code: '206C(1F)', label: 'Motor vehicle over ₹10 lakh', rate: 1 },
    {
        code: '206CC',
        label: 'No PAN furnished',
        rate: 1,
        note: 'Twice the rate, or 1%, whichever is higher.',
    },
];
const codes = (sections) => sections.map((s) => s.code);
export const TDS_SECTION_CODES = codes(TDS_SECTIONS);
export const TCS_SECTION_CODES = codes(TCS_SECTIONS);
export const findTdsSection = (code) => TDS_SECTIONS.find((s) => s.code === code);
export const findTcsSection = (code) => TCS_SECTIONS.find((s) => s.code === code);
/**
 * A section code as stored.
 *
 * NOT a `z.enum` over the catalogue above, deliberately. A record written under
 * last year's section list has to stay readable after this file is edited, and
 * an enum would make a Finance Act change reject historical rows on read. The
 * catalogue drives the picker; the column stores what was chosen.
 */
/** Never empty: a deduction with no section appears in no quarter's return. */
const sectionCodeSchema = z.string().trim().min(1, 'Say which section the TDS was deducted under').max(SECTION_CODE_MAX_LENGTH);
/** A rate as a percentage. 10 means 10%. */
const withholdingRateSchema = z.number().min(0).max(100);
/**
 * TDS as recorded against a payment.
 *
 * All three fields together or none. A rate with no amount cannot be
 * reconciled, and an amount with no section cannot be reported - a deduction
 * that appears in no quarter's return is worse than one that was never
 * recorded, because it silently balances the books while remaining invisible.
 */
export const tdsDeductionSchema = z
    .object({
    section: sectionCodeSchema,
    rate: withholdingRateSchema,
    /** What was ACTUALLY deducted, which the deductor decided. */
    amount: z.string().trim().regex(/^\d+(\.\d{1,2})?$/, 'Use a plain amount like 10000.00'),
    /** Their TAN, printed on the Form 16A this deduction will appear on. */
    deductorTan: z.string().trim().max(15).optional(),
})
    .strict();
/** TCS as charged on a document. The seller computes this one. */
export const tcsChargeSchema = z
    .object({
    section: sectionCodeSchema,
    rate: withholdingRateSchema,
})
    .strict();
//# sourceMappingURL=withholding.js.map